import { useState } from 'react';
import { Lock, ArrowRight, CheckCircle2, Mail, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { PLAN_STATUS_LABEL, planStatusEffective } from '../lib/plans';
import { INCLUDED_IN_ALL } from '../lib/plans';

export default function LockedFeature({ moduleName = 'este módulo' }) {
  const { organization, user } = useAuth();
  const navigate = useNavigate();
  const status = planStatusEffective(organization);
  const isTrial = status === 'trial';

  const [showModal, setShowModal] = useState(false);
  const [phone, setPhone] = useState(user?.telefono || '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post('/plan/contact-request', {
        phone: phone.trim() || null,
        message: message.trim() || null,
      });
      if (data.queued) {
        toast.success(data.message || 'Solicitud enviada. Nos contactaremos pronto.');
      } else {
        toast.warning(data.message || 'Registramos su solicitud, pero no pudimos enviar el correo.');
      }
      setShowModal(false);
      setMessage('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos enviar la solicitud.');
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-3xl mx-auto py-10 px-4" data-testid="locked-feature">
      <div className="border border-[#E5E7EB] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        {/* Header banner */}
        <div className="border-b border-[#E5E7EB] px-6 py-5 bg-[#F0F7F5]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#143A34] text-white flex items-center justify-center">
              <Lock size={18} strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#065F46] font-semibold">
                {isTrial ? 'Modo prueba' : 'Plan ' + (PLAN_STATUS_LABEL[status] || status).toLowerCase()}
              </div>
              <h1 className="text-[22px] font-semibold text-[#0F2A26] tracking-[-0.01em]">
                {moduleName} requiere un plan activo
              </h1>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className="text-[14px] text-[#374151] leading-relaxed">
            {isTrial ? (
              <>
                Su cuenta está en <b>modo prueba</b>. En este momento puede
                usar el <b>Dashboard</b> y hacer el <b>Diagnóstico inicial</b> para conocer su
                nivel de cumplimiento. Para desbloquear el resto de los módulos y empezar a
                cumplir con la Ley 21.719, contrate un plan.
              </>
            ) : (
              <>
                El plan de <b>{organization?.razon_social}</b> está{' '}
                <b>{(PLAN_STATUS_LABEL[status] || status).toLowerCase()}</b>. Contacte a
                Priviax para reactivarlo y recuperar el acceso a todos los módulos.
              </>
            )}
          </div>

          {/* Lo que se desbloquea */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#065F46] font-semibold mb-2">
              Lo que se desbloquea al activar el plan
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
              {INCLUDED_IN_ALL.map((f) => (
                <li key={f} className="flex items-start gap-1.5 text-[13px] text-[#1C2329]">
                  <CheckCircle2 size={13} className="text-[#10B981] mt-[3px] flex-shrink-0" strokeWidth={2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="w-full sm:w-auto px-5 py-2.5 bg-[#143A34] hover:bg-[#0F2A26] text-white text-[13.5px] font-medium inline-flex items-center justify-center gap-2 transition"
              data-testid="locked-cta-contact"
            >
              <Mail size={14} /> Quiero que me contacten
              <ArrowRight size={13} />
            </button>
          </div>

          <div className="pt-2 border-t border-[#E5E7EB]">
            <button
              onClick={() => navigate('/app/dashboard')}
              className="text-[12.5px] text-[#6B7280] hover:text-[#143A34] underline underline-offset-2"
              data-testid="locked-back-dashboard"
            >
              ← Volver al dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Modal contacto */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="contact-modal"
          onClick={() => !sending && setShowModal(false)}
        >
          <div
            className="bg-white w-full max-w-md border border-[#E5E7EB] rounded-md shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-2 text-[#1C2329]">
                <Mail size={16} strokeWidth={1.8} className="text-[#143A34]" />
                <span className="text-[15px] font-semibold">Quiero que me contacten</span>
              </div>
              <button
                type="button"
                onClick={() => !sending && setShowModal(false)}
                className="text-[#6B7280] hover:text-[#1C2329]"
                data-testid="contact-close"
              >
                <X size={16} />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="text-[13px] text-[#374151]">
                Un ejecutivo de Priviax se contactará con usted para coordinar la
                activación de su plan. Confirmemos algunos datos:
              </div>
              <div className="text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded px-3 py-2 bg-[#FAFBFC]">
                <div><b>Empresa:</b> {organization?.razon_social}</div>
                <div><b>RUT:</b> {organization?.rut}</div>
                <div><b>Correo:</b> {user?.email}</div>
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">
                  Teléfono de contacto (opcional)
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+56 9 …"
                  className="pv-input"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">
                  Mensaje (opcional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder="¿Algo puntual que quiera comentar? Ej: cantidad de usuarios, urgencia, dudas del plan."
                  className="pv-input"
                  data-testid="contact-message"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={sending}
                  className="pv-btn-secondary"
                  data-testid="contact-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="pv-btn-primary"
                  data-testid="contact-submit"
                >
                  {sending ? 'Enviando…' : 'Enviar solicitud'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
